import * as React from 'react';
import { toast } from 'react-toastify';
import { formatNumber, splitByUppercase } from '../Common/Utilities';
import { httpGet, httpPost } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import ServerExportWrapper from '../Common/DataGridServerExportWrapper';
import { getFilter, setFilter } from '../Common/Utilities';
import { getGridNumericOperators } from '@mui/x-data-grid';

const numberFilterOperators = getGridNumericOperators().map((op) => {
	const labelMap = {
		'=': 'is equal to',
		'!=': 'is not equal to',
		'>': 'is greater than',
		'>=': 'is greater than or equal to',
		'<': 'is less than',
		'<=': 'is less than or equal to',
		'isEmpty': 'is empty',
		'isNotEmpty': 'is not empty',
	};
	return { ...op, label: labelMap[op.value] ?? op.label };
});

export default class InsertionOrdersReport extends React.Component {
	constructor() {
		super();
		const persisted = getFilter(["Reports", "InsertionOrdersReport"]) || {};
		const nineWeeksAgo = new Date();
		nineWeeksAgo.setDate(nineWeeksAgo.getDate() - 63);

		const defaultFilterModel = {
			items: [{
				field: 'createDate',
				operator: 'after',
				value: nineWeeksAgo.toISOString().split('T')[0]
			}],
			logicOperator: 'and'
		};

		this.state = {
			data: [],
			currentWidth: null,
			filterText: persisted.filterText ?? '',
			lastFilterText: '',
			filterModel: persisted.filterModel ?? defaultFilterModel,
			columnVisibility: persisted.columnVisibility || {},
			tempTableEnabled: false,
			exportData: [],
			dataLoaded: false,
			isServerMode: false,
			defaultFilter: { ...defaultFilterModel.items[0] },
			hasSearched: persisted.hasSearched ?? false,
			sortModel: persisted.sortModel || []

		};
		this.mainGridRef = React.createRef();
		this.searchCallback = this.searchCallback.bind(this);
		this.getFilterItems = this.getFilterItems.bind(this);
		this.fieldInModel = this.fieldInModel.bind(this);
		this.normalizeFilterModel = this.normalizeFilterModel.bind(this);
	}


	componentDidMount() {

		const gridRef = this.mainGridRef.current;
		const { filterModel, filterText, hasSearched } = this.state;
		gridRef.setFilterModel({
			...filterModel,
			quickFilterValues: ['']
		});
		if (gridRef && typeof gridRef.setQuickFilterValues === 'function') {
			gridRef.setQuickFilterValues([filterText]);
		}
		this.fetchInsertionOrders(filterModel, hasSearched ? filterText : '');

		setFilter(['Reports', 'InsertionOrdersReport'], {
			...getFilter(['Reports', 'InsertionsOrderReport']),
			hasSearched,
			filterText,
			filterModel,
			columnVisibility: this.state.columnVisibility
		});
	}

	fetchInsertionOrders(filterModel, searchText, exportData) {
		var that = this;

		this.props.inProgress(true);

		httpPost('/api/reports/insertionorders/', {
			searchFilter: searchText,
			otherFilters: JSON.stringify((filterModel.items || []).
				map(f => ({
					field: splitByUppercase(f.field).join('_'),
					operator: f.operator,
					value: (f.operator === 'isEmpty' || f.operator === 'isNotEmpty') ? true : f.value,
				}))
				.filter(f => f.value !== undefined && f.value !== '')),
			logicOperator: filterModel.logicOperator,
			isAll: !!exportData
		})
			.then((response) => {
				var data = (response.data || []).map((e, i) => { let d = { ...e, rowId: i }; return d });

				// If exporting, transform amount columns to include currency
				if (!!exportData) {
					const exportDataTransformed = data.map(row => ({
						...row,
						poAmount: formatNumber(row.poAmount) + " " + (row.currencyCode || ""),
						consumedAmount: formatNumber(row.consumedAmount) + " " + (row.currencyCode || "")
					}));
					that.setState({ ...this.state, exportData: exportDataTransformed, dataLoaded: true });
				}
				else {
					that.setState({ data: data });
				}

				this.props.inProgress(false);
			})
			.catch((error) => {
				this.props.inProgress(false);
				if (!!exportData) {
					that.setState({ ...this.state, tempTableEnabled: false });
				}
				toast.error('An error occurred while fetching the records');
				console.error('There was an error!', error);
			});
	}


	searchCallback = (searchText) => {
		const trimmedText = (searchText || "").trim();
		const prevText = this.state.lastFilterText;
		if (trimmedText === "" && prevText !== "") {
			this.setState({ lastFilterText: "" }, () => {
				this.fetchInsertionOrders(this.state.filterModel, searchText);

			});
			return;
		}
		if (trimmedText !== "" && trimmedText !== prevText) {
			this.setState({ lastFilterText: trimmedText }, () => {
				this.fetchInsertionOrders(this.state.filterModel, searchText);

			});
		}
	}
	fieldInModel(model, fieldName) {
		const relevantOperators = ['after', 'onOrAfter', 'before', 'onOrBefore'];
		return model.items?.some(f =>
			f.field === fieldName && (
				!f.operator || relevantOperators.includes(f.operator)
			)
		);
	}
	normalizeFilterModel(model) {
		return {
			items: [...(model?.items || [])],
			logicOperator: model?.logicOperator || 'and'
		};
	}

	handleFilterPanelSearch = (incomingFilterModel, quickFilterText = '') => {

		const normalizedModel = this.normalizeFilterModel(incomingFilterModel);
		this.setState({
			filterModel: normalizedModel,
			filterText: quickFilterText,
			isServerMode: false,
			hasSearched: true
		}, () => {
			setFilter(['Reports', 'InsertionOrdersReport'], {
				filterModel: normalizedModel,
				filterText: quickFilterText,
				columnVisibility: this.state.columnVisibility,
				hasSearched: true,
			});
			this.fetchInsertionOrders(normalizedModel, quickFilterText);
		});
	};
	getFilterItems(filterModel) {
		return filterModel;
	}
	render() {
		const cellStyle = { paddingLeft: 8, paddingRight: 45 };
		const filterCellStyle = { textAlign: 'right', paddingRight: 45 };
		const headerStyle = { textAlign: 'right' };

		const columns = [
			{
				headerName: 'Vendor',
				field: 'vendor',
				width: 200
			},
			{
				headerName: 'Currency',
				field: 'currencyCode',
				width: 80
			},
			{
				headerName: 'IO/PO Number',
				field: 'poCode',
				width: 150,
				hideable: false,
				valueGetter: (params) => {
					const poCode = params.row.poCode || '';
					const mediaType = params.row.mediaType || '';
					return mediaType ? `${poCode} (${mediaType})` : poCode;
				},
				renderCell: (rowData) => (
					<div style={{ wordBreak: 'break-word' }}>
						<div>{rowData.row.poCode}</div>
						<div style={{ fontSize: '0.9em', color: 'grey', fontWeight: 'bold' }}>{rowData.row.mediaType}</div>
					</div>
				)
			},
			{
				headerName: 'IO/PO Amount',
				field: 'poAmount',
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				width: 150,
				filterOperators: numberFilterOperators,
				valueGetter: (params) => params.row.poAmount,
				renderCell: (rowData) => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.poAmount) + " " + rowData.row.currencyCode}</span>,
				valueFormatter: (scope) => {
					var api = scope.api;
					var row = api.getRow(scope.id);
					return formatNumber(row[scope.field]) + " " + (row['currencyCode'] || "");
				},
				getExportValue: (row) => formatNumber(row.poAmount) + " " + (row.currencyCode || "")
			},
			{
				headerName: 'IO/PO Consumed Amount',
				field: 'consumedAmount',
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				minWidth: 150,
				flex: 1,
				filterOperators: numberFilterOperators,
				valueGetter: (params) => params.row.consumedAmount,
				valueFormatter: (scope) => {
					var api = scope.api;
					var row = api.getRow(scope.id);
					return formatNumber(row[scope.field]) + " " + (row['currencyCode'] || "");
				},
				renderCell: (rowData) => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.consumedAmount) + " " + rowData.row.currencyCode}</span>,
				getExportValue: (row) => formatNumber(row.consumedAmount) + " " + (row.currencyCode || "")
			},
			{
				headerName: 'IO/PO Remaining Amount',
				field: 'remainingAmount',
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				width: 150,
				filterOperators: numberFilterOperators,
				valueGetter: (params) => params.row.remainingIoAmount,
				renderCell: (rowData) => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.remainingIoAmount) + " " + rowData.row.currencyCode}</span>,
				valueFormatter: (scope) => {
					var api = scope.api;
					var row = api.getRow(scope.id);
					return formatNumber(row[scope.field]) + " " + (row['currencyCode'] || "");
				},
				getExportValue: (row) => formatNumber(row.remainingIoAmount) + " " + (row.currencyCode || "")
			},
			{
				headerName: 'Delivery Amount',
				field: 'deliveryAmount',
				type: 'number',
				align: 'right',
				headerAlign: 'right',
				width: 150,
				filterOperators: numberFilterOperators,
				valueGetter: (params) => params.row.deliveryAmount,
				renderCell: (rowData) => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.deliveryAmount) + " " + rowData.row.currencyCode}</span>,
				valueFormatter: (scope) => {
					var api = scope.api;
					var row = api.getRow(scope.id);
					return formatNumber(row[scope.field]) + " " + (row['currencyCode'] || "");
				},
				getExportValue: (row) => formatNumber(row.deliveryAmount) + " " + (row.currencyCode || "")
			},
			{
				headerName: 'Client Name',
				field: 'clientName',
				minWidth: 150,
				flex: 1
			},
			{
				headerName: 'Job Code',
				field: 'jobCode',
				width: 150,
			},
			{
				headerName: 'Campaign Name',
				field: 'campaignName',
				width: 250,
			},
			{
				headerName: 'Media Plan',
				field: 'mediaPlanName',
				width: 200,
			},
			{
				headerName: 'Create Date',
				field: 'createDate',
				type: 'date',
				hideable: false,
				valueGetter: ({ value }) => value && new Date(value),
				width: 200
			},

		];

		return (
			<div>
				<h1>Insertion Orders</h1>
				<div className="form-group">
					<div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
						<EnhancedDataGrid rows={this.state.data}
							disableColumnMenu
							pagination
							pageSize={10}
							columns={columns}
							getRowId={(row) => row.rowId}
							disableSelectionOnClick
							options={{
								pageSize: 10,
								pageSizeOptions: [10, 20, 30, 40, 50],
								maxBodyHeight: 670,
								filtering: true,
								columnsButton: true,
								exportButton: true,
								exportAllData: true,
								exportFileName: "InsertionOrders"
							}}
							gridRef={this.mainGridRef}
							serverexport={true}
							searchCallback={null}
							setFilterText={(text) => {

								const trimmedText = text?.trim() || '';
								const stored = getFilter(['Reports', 'InsertionOrdersReport']) || {};

								if (trimmedText === stored.filterText) return;

								const shouldResetSearch = stored.hasSearched && stored.filterText !== trimmedText;
								const nextHasSearched = shouldResetSearch ? false : stored.hasSearched;
								this.setState({ ...this.state, filterText: trimmedText, hasSearched: nextHasSearched })
								setFilter(['Reports', 'InsertionOrdersReport'], {
									...stored,
									filterText: trimmedText,
									hasSearched: nextHasSearched
								});
							}}
							highlightSearchTextOnMainScreenGrid={true}
							onColumnVisbilityModelChange={(model) => {
								this.setState({ columnVisibility: model });
								setFilter(['Reports', 'InsertionOrdersReport'], {
									...getFilter(['Reports', 'InsertionOrdersReport']),
									columnVisibility: model,
								});
							}}
							onSortModelChange={(model) => {
								this.setState({ sortModel: model });
								setFilter(['Reports', 'InsertionOrdersReport', 'sortModel'], model);
							}}
							sortModel={this.state.sortModel}
							columnVisibilityModel={this.state.columnVisibility}
							onFilterPanelSearch={(filterModel, quickFilterText) => {
								this.handleFilterPanelSearch(filterModel, quickFilterText);
							}}
							customFilterPanel={true}
							setFilterModel={(model) => {
								this.setState({ ...this.state, filterModel: { items: [...model.items], logicOperator: model.logicOperator } })
							}}
							customServerFilter={() => this.state.isServerMode ? () => { } : undefined}
							exportServerCallback={() => {
								this.setState(prev => {
									return { ...prev, tempTableEnabled: true }
								}, () => {
									this.fetchInsertionOrders(this.state.filterModel, this.state.filterText, true);
								})
							}}
							footerMessage={''}
						/>
						{
							this.state.tempTableEnabled ?

								<ServerExportWrapper
									rows={this.state.exportData}
									initialState={{
										columns: {
											columnVisibilityModel: {
												...this.mainGridRef.current.exportState().columns.columnVisibilityModel
											},
										},
									}}
									columns={columns}
									options={{
										exportFileName: "InsertionOrders"
									}}

									dataLoaded={this.state.dataLoaded}
									getRowId={(row) => row.rowId}
									resetStates={() => {
										this.setState({
											...this.state,
											tempTableEnabled: false,
											dataLoaded: false,
											exportData: []
										})
									}}
								/>
								:
								<></>
						}
					</div>
				</div>
			</div>
		);
	}
}